import { useEffect, useRef } from 'react';

/**
 * `useAutoRefresh` — every read screen stays current on its own (owner, 2026-10-13: "no refresh button; the data must be
 * live, or at least re-read every X seconds behind the scenes"). It re-runs the screen's own `load` every `intervalMs`
 * **only while the tab is visible**, and at once when the tab becomes visible again. A hidden tab makes no request, and a
 * read still in flight is never overlapped by the next tick. The first read stays the screen's (its mount / filter effect):
 * this hook only keeps it fresh, so it never doubles the initial request.
 *
 * The screen keeps showing what it has while a background read runs (no loading flash); its own `load` already discards
 * a late answer to an older filter (the seq guard every screen uses).
 */
export const AUTO_REFRESH_MS = {
  /** Live operations (live map, dispatch, rides in progress, a kitchen queue). */
  live: 10_000,
  /** Work queues (approvals, payouts, tickets, alerts). */
  queue: 30_000,
  /** Reports and period panels. */
  report: 60_000,
} as const;

export interface AutoRefreshOptions {
  readonly intervalMs?: number;
  /** When false nothing is re-read (no market in scope, an invalid filter, a form being edited). */
  readonly enabled?: boolean;
}

export function useAutoRefresh(load: () => unknown, { intervalMs = AUTO_REFRESH_MS.queue, enabled = true }: AutoRefreshOptions = {}): void {
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) return undefined;
    let inFlight = false;
    const tick = (): void => {
      if (inFlight || (typeof document !== 'undefined' && document.visibilityState !== 'visible')) return;
      inFlight = true;
      void Promise.resolve()
        .then(() => loadRef.current())
        .catch(() => undefined)
        .finally(() => {
          inFlight = false;
        });
    };
    const timer = globalThis.setInterval(tick, Math.max(1_000, intervalMs));
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') tick();
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
    return () => {
      globalThis.clearInterval(timer);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs, enabled]);
}
